/**
 * The fixed sources of the film's relief (#140, 1.129.0), apart from
 * `terrainGround.ts` so that naming them loads no MapLibre: the film without
 * relief imports this file, and MapLibre comes only with the switch.
 *
 * **A fixed list and no setting**, as `MapSources`: a typed address would be
 * a foreign request in the author's browser, and the CSP names exactly these
 * two hosts (`server/.htaccess`, `tests/CspTest.php`).
 */
export const TERRAIN_STYLE = 'https://tiles.openfreemap.org/styles/liberty'
export const TERRAIN_HEIGHTS = 'https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png'

/**
 * The licence note in the picture. OpenFreeMap asks for exactly this in print
 * and video; the heights need their source named. A note, not a sentence of
 * the app -- translated it would no longer be the note (`KEIN_TEXT`).
 */
export const TERRAIN_CREDIT = '© OpenMapTiles · Data from OpenStreetMap · Höhen: Mapzen/AWS Terrain Tiles'

/**
 * Place names in the language of the country, with a Latin transliteration
 * under a name in another script (1.145.0). From use: "ist es möglich die
 * MapLibre Karte in anderen Sprachen anzuzeigen. oder: in der jeweiligen
 * Landessprache. also z.B. Deutschland, France, United Kingdom", decided:
 * "Landessprache mit lateinischer Umschrift". The Liberty style shows the
 * English name first ("Munich"), the raster maps of the film without relief
 * show the local one; now both say "München".
 *
 * Nothing more goes out for it: the names in every language are in the same
 * vector tiles (OpenMapTiles: `name`, `name:latin`, `name:nonlatin`,
 * `name_int`), and which one stands is the style's choice, made here. A
 * script MapLibre cannot set without its right-to-left plugin (Arabic,
 * Hebrew) would come out as loose letters; there only the transliteration
 * stands (`is-supported-script`). Chinese and Japanese MapLibre sets with the
 * browser's own fonts.
 */
export const LOCAL_LABEL: unknown[] = [
  'case',
  ['all', ['has', 'name:nonlatin'], ['is-supported-script', ['get', 'name:nonlatin']]],
  ['case',
    ['has', 'name:latin'], ['concat', ['get', 'name:nonlatin'], '\n', ['get', 'name:latin']],
    ['get', 'name:nonlatin']],
  ['coalesce', ['get', 'name:latin'], ['get', 'name_int'], ['get', 'name'], ''],
]

/**
 * The layers of a style that label something with its name: a symbol layer
 * whose text names a `name` field in any form (`{name_en}`, `["get",
 * "name:latin"]`). Road numbers and house numbers keep their own text.
 */
export function namedLayers(layers: readonly { id: string; type: string; layout?: Record<string, unknown> }[]): string[] {
  return layers
    .filter((layer) => layer.type === 'symbol' && /name/.test(JSON.stringify(layer.layout?.['text-field'] ?? '')))
    .map((layer) => layer.id)
}
